import { useWallet, Wallet } from '@provablehq/aleo-wallet-adaptor-react';
import { ALEO_WALLET_ADAPTER } from '@/configs/bridge/chainInfo';
import { IPuzzleAleoRecord } from '@/interfaces/bridge/record.interface';
import { WalletStatus } from '@/interfaces/bridge/wallets.interface';
import { useEffect, useMemo, useState } from 'react';
import {
  BridgeDecryptedRecordsResult,
  BRIDGE_ALEO_WALLET,
  emptyBridgeDecryptedRecordsResult,
  filterPuzzleTokenRecords,
  filterShieldTokenRecords,
  getBridgeAleoWalletType
} from '@/utils/bridge/aleoWalletRecord';
import { ShieldRecord } from '@/utils/shieldRecord';

/**
 * Unified Aleo wallet hook for both money-market and bridge contexts.
 *
 * Money-market callers use the base wallet API (`address`, `connected`,
 * `requestTransaction`, `connect`, `reset`).  Bridge callers also consume
 * `getDecryptedRecords`, `walletRecordType`, `getStartedWallet`, and `status`.
 */
export const useAleoWallet = () => {
  const aleoWalletConfig = useWallet();

  const [status, setStatus] = useState<(typeof WalletStatus)[keyof typeof WalletStatus]>(
    WalletStatus.DISCONNECTED
  );

  const walletRecordType = useMemo(
    () => getBridgeAleoWalletType(aleoWalletConfig.wallet?.adapter.name),
    [aleoWalletConfig.wallet?.adapter.name]
  );

  useEffect(() => {
    if (aleoWalletConfig.connected) setStatus(WalletStatus.CONNECTED);
    else if (aleoWalletConfig.connecting) setStatus(WalletStatus.CONNECTING);
    else setStatus(WalletStatus.DISCONNECTED);
  }, [aleoWalletConfig.connected, aleoWalletConfig.connecting]);

  const connect = async (name: Parameters<typeof aleoWalletConfig.selectWallet>[0] | string) => {
    try {
      if (aleoWalletConfig.connected) {
        await aleoWalletConfig.disconnect();
      } else {
        aleoWalletConfig.selectWallet(name as Parameters<typeof aleoWalletConfig.selectWallet>[0]);
        aleoWalletConfig.connect(ALEO_WALLET_ADAPTER as Parameters<typeof aleoWalletConfig.connect>[0]);
      }
    } catch (error) {
      console.error('Failed to connect to wallet:', error);
    }
  };

  const reset = () => aleoWalletConfig.disconnect();

  const [installedWallets, otherWallets] = useMemo(() => {
    const installed: Wallet[] = [];
    const notDetected: Wallet[] = [];
    const loadable: Wallet[] = [];

    for (const wallet of aleoWalletConfig.wallets) {
      if (wallet.readyState === 'NotDetected') notDetected.push(wallet);
      else if (wallet.readyState === 'Loadable') loadable.push(wallet);
      else if (wallet.readyState === 'Installed') installed.push(wallet);
    }

    return [installed, [...loadable, ...notDetected]];
  }, [aleoWalletConfig.wallets]);

  const getStartedWallet = useMemo(
    () =>
      installedWallets.length
        ? installedWallets[0]!
        : aleoWalletConfig.wallets.find((w) => w.adapter.name === 'Leo Wallet') ||
          aleoWalletConfig.wallets.find((w) => w.adapter.name === 'Phantom') ||
          aleoWalletConfig.wallets.find((w) => w.readyState === 'Loadable') ||
          otherWallets[0]!,
    [installedWallets, aleoWalletConfig.wallets, otherWallets]
  );

  async function getDecryptedRecords(
    amount: bigint,
    token_id: bigint,
    token_program: string
  ): Promise<BridgeDecryptedRecordsResult> {
    if (!aleoWalletConfig.address || !aleoWalletConfig.requestRecords || !walletRecordType) {
      return emptyBridgeDecryptedRecordsResult();
    }

    switch (walletRecordType) {
      case BRIDGE_ALEO_WALLET.PUZZLE: {
        const records = (await aleoWalletConfig.requestRecords(token_program.trim(), true)) as IPuzzleAleoRecord[];
        return filterPuzzleTokenRecords(records, amount, token_id);
      }
      case BRIDGE_ALEO_WALLET.SHIELD: {
        const records = (await aleoWalletConfig.requestRecords(token_program.trim(), true)) as ShieldRecord[];
        return filterShieldTokenRecords(records, amount, token_id);
      }
      default:
        return emptyBridgeDecryptedRecordsResult();
    }
  }

  return {
    ...aleoWalletConfig,
    requestTransaction: aleoWalletConfig.executeTransaction,
    status,
    connect,
    reset,
    walletRecordType,
    getStartedWallet,
    getDecryptedRecords
  };
};
